import React, { useState, useEffect, useRef } from 'react';
import { PlxsButton, PlxsHeading, PlxsText, PlxsTextarea } from '@plexlogic/plexus-ui';
import apiService from '../../services/api';
import WizardSpinner from '../WizardSpinner';
import { getActionChipTone } from '../../utils/actionRegistry';
import { formatRelativeTime, formatDateTime } from '../../utils/dateTimeUtils';
import './ActivityFeed.css';

interface ActivityLog {
  id: string;
  entity_type: string;
  entity_id: string;
  action_type: string;
  action_description: string | null;
  actor_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_type: string;
  created_at: string;
  comment_text: string | null;
  is_comment: boolean;
  metadata: Record<string, any>;
}

interface ActivityFeedProps {
  drawId: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ drawId }) => {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  const loadActivities = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      setError(null);
      const data = await apiService.getDrawActivityFeed(drawId, 50);
      if (!isMountedRef.current) return;
      setActivities(data);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError("Failed to load activity feed");
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    loadActivities();
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawId]);

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!comment.trim()) return;

    try {
      setSubmitting(true);
      await apiService.addDrawComment(drawId, comment.trim());
      if (!isMountedRef.current) return;
      setComment("");
      // Reload activities to show new comment
      await loadActivities();
    } catch (err) {
      if (!isMountedRef.current) return;
      setError("Failed to post comment");
    } finally {
      if (!isMountedRef.current) return;
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    loadActivities();
  };

  const formatTimestamp = (isoString: string) => {
    // Use centralized datetime formatting with relative time for recent activities
    const relativeTime = formatRelativeTime(isoString);
    
    // If more than 7 days old, show full date instead
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays >= 7) {
      return formatDateTime(isoString).date;
    }
    
    return relativeTime;
  };

  const formatDescriptionWithTimestamps = (description: string | null): string => {
    if (!description) return '';
    
    // Match ISO 8601 timestamps in the description (e.g., 2025-10-30T14:30:00+00:00)
    const isoTimestampRegex = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2})/g;
    
    return description.replace(isoTimestampRegex, (match) => {
      try {
        const formatted = formatDateTime(match);
        return `${formatted.date} at ${formatted.time}`;
      } catch {
        // If parsing fails, return original
        return match;
      }
    });
  };

  return (
    <div className="activity-feed">
      {/* Header */}
      <div className="activity-feed-header">
        <PlxsHeading element="h3">My activity</PlxsHeading>
        <PlxsText>Recent events and comments</PlxsText>
      </div>

      {/* Comment Input */}
      <div className="comment-input-section">
        <div className="comment-label-container">
          <label className="comment-label">Add a comment</label>
        </div>
        <PlxsTextarea
          dataTestId="draw-detail-activity-comment-textarea"
          placeholder="Type your comment here"
          styleOverrides={{ hidePlaceholderEllipsis: true }}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          isDisabled={submitting}
        />
        <div className="comment-actions">
          <PlxsButton
            dataTestId="draw-detail-post-comment-button"
            label={submitting ? 'Posting...' : 'Post comment'}
            onClick={handleCommentSubmit}
            variant="primary"
            tone="neutral"
            isDisabled={!comment.trim() || submitting}
            isLoading={submitting}
          />
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="activity-timeline">
        {loading && (
          <div className="activity-loading">
            <WizardSpinner size={32} dataTestId="draw-detail-activity-loading-spinner" />
            <PlxsText>Loading activities...</PlxsText>
          </div>
        )}

        {error && (
          <div className="activity-error">
            <PlxsText>{error}</PlxsText>
            <PlxsButton
              dataTestId="draw-detail-activity-retry-button"
              label="Retry"
              onClick={handleRetry}
              variant="secondary"
              tone="brand"
            />
          </div>
        )}

        {!loading && !error && activities.length === 0 && (
          <div className="activity-empty">
            <PlxsText>No activity yet</PlxsText>
          </div>
        )}

        {!loading && !error && activities.length > 0 && (
          <div className="activity-list">
            {activities.map((activity) => {
              const tone = activity.is_comment ? 'brand' : getActionChipTone(activity.action_type);
              const actorName = activity.actor_name || activity.actor_email || "System";
              
              return (
                <div
                  key={activity.id}
                  className={`activity-item activity-item-${tone}`}
                >
                  <div className="activity-content">
                    <div className="activity-header">
                      <div className="activity-title">
                        <span className="activity-actor">{actorName}</span>
                      </div>
                      <span className="activity-timestamp">
                        {formatTimestamp(activity.created_at)}
                      </span>
                    </div>
                    {(activity.is_comment ? activity.comment_text : activity.action_description) && (
                      <div className="activity-description">
                        {activity.is_comment 
                          ? activity.comment_text 
                          : formatDescriptionWithTimestamps(activity.action_description)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
